import {
  defaultOverduePaymentState,
  OVERDUE_PAYMENT_STATE_STORAGE_KEY,
  overduePaymentAtom,
} from '@/atoms/overdue-payment';
import { hasBeenDismissed } from '@/components/PagePopup/utils/hasBeenDismissed';
import { getSessionId } from '@/pages/api/v1/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { useSettings } from '../state/useSettings';

export const OVERDUE_PAYMENT_POPUP_STORAGE_KEY = 'failed-payment-popup';

export const useOverduePayment = () => {
  const { settings } = useSettings();

  const {
    sessions: previousSessions,
    showPopup,
    showBanner,
    showPopover,
  } = useAtomValue(overduePaymentAtom);

  const setOverduePaymentState = useSetAtom(overduePaymentAtom);

  const userIsLoggedIn = useMemo(
    () => !!settings?.user?.is_authenticated,
    [settings]
  );

  const overduePayment = useMemo(
    () => settings?.subscriptionData?.overduePayment,
    [settings?.subscriptionData?.overduePayment]
  );

  const shouldShowPopover = useMemo(() => {
    if (!previousSessions || previousSessions.length === 0) {
      return false;
    }

    const sessionId = getSessionId();

    const isCurrentSession = previousSessions.find(
      (existingSession) => existingSession.id === sessionId
    );

    if (isCurrentSession) {
      return false;
    }

    return true;
  }, [previousSessions]);

  const cleanupStorage = useCallback(() => {
    localStorage.removeItem(OVERDUE_PAYMENT_POPUP_STORAGE_KEY);
    localStorage.removeItem(OVERDUE_PAYMENT_STATE_STORAGE_KEY);
    sessionStorage.removeItem('session_id');
  }, []);

  const revalidateOverduePaymentPopup = useCallback(() => {
    if (!settings) {
      return;
    }

    if (userIsLoggedIn) {
      // If there's no overdue payment, don't show the popup and reset to default state
      if (!overduePayment) {
        setOverduePaymentState(defaultOverduePaymentState);
        cleanupStorage();
        return;
      }

      if (
        hasBeenDismissed({
          storageType: 'localStorage',
          storageKey: OVERDUE_PAYMENT_POPUP_STORAGE_KEY,
        })
      ) {
        setOverduePaymentState((prev) => ({
          ...prev,
          showPopup: false,
          showBanner: true,
          showPopover: shouldShowPopover,
          overduePaymentId: overduePayment.id,
        }));
        return;
      }

      // Reset state to show the popup if there's an overdue payment
      setOverduePaymentState({
        showPopup: true,
        showBanner: false,
        showPopover: false,
        overduePaymentId: overduePayment.id,
      });
      return;
    }

    setOverduePaymentState(defaultOverduePaymentState);
  }, [
    overduePayment,
    settings,
    userIsLoggedIn,
    setOverduePaymentState,
    shouldShowPopover,
    cleanupStorage,
  ]);

  useEffect(() => {
    revalidateOverduePaymentPopup();
  }, [revalidateOverduePaymentPopup]);

  return {
    showPopup,
    showBanner,
    showPopover,
    overduePayment,
    setOverduePaymentState,
  };
};
